import React, { useContext, useEffect, useState } from 'react';
import {
  Checkbox,
  Divider,
  Indicator,
  NumberFormatter,
  SimpleGrid
} from '@mantine/core';
import { Context as HelperContext } from '../../../providers/HelperContextProvider';
import AppFlexbox from '../../common/AppFlexbox';
import AppText from '../../common/AppText';
import AppCard from '../../common/AppCard';
import AppStack from '../../common/AppStack';
import { singularPluralFormat } from '../../../helpers/format';
import classes from '../../../styles/nestedStyles.module.css';
import AppImage from '../../common/AppImage';
import { useMediaQueries } from '../../../helpers/hooks';
import ResponsiveWebstoreModal from '../ecommerce/webstore/ResponsiveWebstoreModal';
import { useRegCartController } from '../../../helpers/shop/useRegCartController';

const ChangeShippingModal = () => {
  const { state: helperState, toggleWebstoreChangeShippingModal } = useContext(
    HelperContext
  );
  const { userCart, cartShippingController } = useRegCartController();
  const { onChangeShippingCosts } = cartShippingController;

  const [formState, setFormState] = useState({ shippingOptions: [] });
  const mediaQueries = useMediaQueries();

  const shippingCostsWithProducts =
    userCart?.ecomStoreCart?.shippingCosts?.map((c) => ({
      ...c,
      products: [
        ...userCart.ecomStoreCart.ecomStoreCartProducts.filter((p) =>
          c.ecomProductIds.includes(p.pkEcomStoreCartProduct)
        )
      ]
    })) ?? [];

  useEffect(
    () => () => {
      if (helperState.webstore.changeShippingModal.isOpen) {
        // eslint-disable-next-line no-use-before-define
        onClose();
      }
    },
    []
  );

  useEffect(() => {
    if (helperState.webstore.changeShippingModal.isOpen) {
      setFormState({
        shippingOptions: userCart?.cart?.shippingCosts.map((s) => ({
          ecomExternalConnectionExternalId: s.ecomExternalConnectionExternalId,
          fkEcomExternalConnectionType: s.fkEcomExternalConnectionType,
          value: s.value,
          label: s.label
        }))
      });
    }
  }, [helperState.webstore.changeShippingModal.isOpen]);

  const onClose = () => {
    if (
      formState.shippingOptions.some((s) => {
        const cartOption = userCart?.cart?.shippingCosts.find(
          (f) =>
            f.fkEcomExternalConnectionType === s.fkEcomExternalConnectionType &&
            f.ecomExternalConnectionExternalId ===
              s.ecomExternalConnectionExternalId
        );

        return !cartOption || s.value !== cartOption.value;
      })
    ) {
      onChangeShippingCosts({ shippingOptions: formState.shippingOptions });
    }

    toggleWebstoreChangeShippingModal({ isOpen: false });
  };

  return (
    <ResponsiveWebstoreModal
      alignModal="center"
      formSectionProps={{
        onSubmit: () => {
          onClose();
        },
        submitTitle: 'Apply',
        submitColor: 'blue',
        isCancelHidden: true,
        style: { gap: 20, paddingBottom: 20 }
      }}
      isOpen={helperState.webstore.changeShippingModal.isOpen}
      mediaQueries={mediaQueries}
      onClose={onClose}
      radius={0}
      size={600}
      title="Change shipping"
    >
      <AppStack style={{ gap: 20 }}>
        {shippingCostsWithProducts.map((s) => {
          const selectedShippingCost = formState.shippingOptions.find(
            (f) =>
              f.ecomExternalConnectionExternalId ===
                s.ecomExternalConnectionExternalId &&
              f.fkEcomExternalConnectionType === s.fkEcomExternalConnectionType
          );

          return (
            <AppCard
              key={`${s.ecomExternalConnectionExternalId}-${s.fkEcomExternalConnectionType}`}
              radius={8}
              shadow="lg"
              style={{ padding: 0 }}
              withBorder
            >
              <AppStack style={{ gap: 0 }}>
                <AppFlexbox
                  style={{ padding: '16px 8px', backgroundColor: '#f3f3f3' }}
                >
                  <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                    Shipping for{' '}
                    {singularPluralFormat(
                      s.products.reduce((r, c) => r + c.count, 0),
                      'item',
                      'items'
                    )}
                  </AppText>
                </AppFlexbox>
                <Divider />
                {s.shippingOptions.map((option, index) => (
                  <React.Fragment key={option.value}>
                    {index > 0 && <Divider />}
                    <AppFlexbox
                      className={classes['hover-action-card']}
                      onClick={() => {
                        setFormState({
                          ...formState,
                          shippingOptions: [
                            ...formState.shippingOptions.filter(
                              (f) =>
                                f.ecomExternalConnectionExternalId !==
                                  s.ecomExternalConnectionExternalId ||
                                f.fkEcomExternalConnectionType !==
                                  s.fkEcomExternalConnectionType
                            ),
                            {
                              ecomExternalConnectionExternalId:
                                s.ecomExternalConnectionExternalId,
                              fkEcomExternalConnectionType:
                                s.fkEcomExternalConnectionType,
                              value: option.label,
                              label: option.label
                            }
                          ]
                        });
                      }}
                      style={{
                        gap: 8,
                        padding: 8,
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <AppFlexbox style={{ gap: 8, alignItems: 'center' }}>
                        <Checkbox
                          checked={
                            selectedShippingCost?.value === option.value ||
                            selectedShippingCost?.value === option.label
                          }
                          onChange={() => {}}
                        />
                        <AppStack style={{ gap: 0 }}>
                          <AppText style={{ fontSize: 14 }}>
                            {option.label}
                          </AppText>
                        </AppStack>
                      </AppFlexbox>

                      <AppText style={{ fontSize: 14, fontWeight: 700 }}>
                        <NumberFormatter
                          decimalScale={2}
                          displayType="text"
                          fixedDecimalScale
                          prefix="$"
                          thousandSeparator
                          value={option.cost / 100}
                        />
                      </AppText>
                    </AppFlexbox>
                  </React.Fragment>
                ))}
                <Divider />
                <AppFlexbox style={{ padding: '16px 8px' }}>
                  <SimpleGrid cols={3}>
                    {s.products.map((p) => (
                      <AppStack
                        key={p.pkEcomStoreCartProduct}
                        style={{ gap: 0 }}
                      >
                        <Indicator inline label={p.count} size={22}>
                          <AppStack
                            style={{
                              height: 100,
                              width: 100,
                              borderRadius: 8,
                              overflow: 'hidden',
                              border: '1px solid #E1E1E1'
                            }}
                          >
                            <AppImage
                              src={
                                p.ecomStoreProductVariant
                                  .ecomVendorProductVariant.previewImage ??
                                p.ecomStoreProductVariant.ecomStoreProduct
                                  .ecomVendorProduct.previewImage
                              }
                            />
                          </AppStack>
                        </Indicator>
                      </AppStack>
                    ))}
                  </SimpleGrid>
                </AppFlexbox>
              </AppStack>
            </AppCard>
          );
        })}
      </AppStack>
    </ResponsiveWebstoreModal>
  );
};

ChangeShippingModal.propTypes = {};

export default ChangeShippingModal;
